import { ApiService } from './api';

// Types for Developer Dashboard
export type DeveloperTaskStatus =
  | 'backlog'
  | 'todo'
  | 'in_progress'
  | 'in_review'
  | 'testing'
  | 'done'
  | 'cancelled';

export type DeveloperTaskPriority = 'low' | 'normal' | 'high' | 'urgent';

export interface DeveloperTask {
  id: number;
  title: string;
  description: string | null;
  project: string;
  projectId?: number | null;
  priority: DeveloperTaskPriority;
  status: DeveloperTaskStatus;
  type: string;
  dueDate: string | null;
  startDate?: string | null;
  completedDate?: string | null;
  assignedAt?: string | null;
  updatedAt?: string | null;
  storyPoints?: number | null;
  estimatedHours?: number | null;
  loggedHours?: number | null;
  // Additional fields for UI components
  assignee?: {
    id: number;
    name: string;
    avatar?: string | null;
  } | null;
  commentsCount?: number | null;
  attachmentsCount?: number | null;
}

export interface DeveloperProject {
  id: number;
  name: string;
  client: string;
  status: string;
  progress: number;
  technologies: string[];
  tasksTotal: number;
  tasksCompleted: number;
  projectManager?: string | null;
  lastCommit?: string | null;
  priority?: string | null;
  description?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  created_at?: string;
  updated_at?: string;
  // GitHub integration fields
  githubRepo?: string | null;
  githubBranch?: string | null;
  openPRs?: number | null;
  openIssues?: number | null;
}

export interface DeveloperStats {
  activeTasks: number;
  completedProjects: number;
  codeReviews: number;
  hoursLogged: number;
  thisWeekHours?: number | null;
  completionRate?: number | null;
  totalTasks?: number | null;
  tasksCompleted?: number | null;
}

export interface DeveloperDashboardData {
  user: {
    id: number;
    name: string;
    email: string;
    role: string;
  };
  projects: DeveloperProject[];
  tasks: DeveloperTask[];
  stats: DeveloperStats;
}

/**
 * Developer Dashboard API Service
 * Handles all API calls related to developer dashboard functionality
 */
export class DeveloperDashboardService {
  /**
   * Get complete developer dashboard data
   */
  static async getDashboardData(): Promise<DeveloperDashboardData> {
    try {
      const response = await ApiService.get<{
        success: boolean;
        data: DeveloperDashboardData;
      }>('/developer/dashboard');
      
      return response.data;
    } catch (error) {
      console.error('Error fetching developer dashboard data:', error);
      throw new Error('Failed to load developer dashboard data');
    }
  }

  /**
   * Get developer's tasks with optional filtering
   */
  static async getTasks(filters?: {
    status?: string;
    priority?: string;
    project?: string;
  }): Promise<DeveloperTask[]> {
    try {
      const response = await ApiService.get<{
        success: boolean;
        data: DeveloperTask[];
        meta: {
          total: number;
          filtered: number;
        };
      }>('/developer/tasks', { params: filters });
      
      return response.data;
    } catch (error) {
      console.error('Error fetching developer tasks:', error);
      throw new Error('Failed to load tasks');
    }
  }

  /**
   * Get developer's projects
   */
  static async getProjects(): Promise<DeveloperProject[]> {
    try {
      console.log('DeveloperDashboardService.getProjects: Calling /developer/projects');
      const response = await ApiService.get<{
        success: boolean;
        data: DeveloperProject[];
        meta?: {
          total: number;
        };
      }>('/developer/projects');
      
      console.log('DeveloperDashboardService.getProjects: Response received', {
        success: response.success,
        projectCount: response.data?.length,
        projects: response.data?.map(p => ({ id: p.id, name: p.name }))
      });
      
      return response.data;
    } catch (error: any) {
      console.error('DeveloperDashboardService.getProjects: Error', {
        error,
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      throw new Error('Failed to load projects');
    }
  }

  /**
   * Get developer statistics
   */
  static async getStats(): Promise<DeveloperStats> {
    try {
      const response = await ApiService.get<{
        success: boolean;
        data: DeveloperStats;
      }>('/developer/stats');
      
      return response.data;
    } catch (error) {
      console.error('Error fetching developer stats:', error);
      throw new Error('Failed to load statistics');
    }
  }

  /**
   * Update task status (when Task model is implemented)
   */
  static async updateTaskStatus(taskId: number, status: string): Promise<any> {
    try {
      console.log('DeveloperDashboardService.updateTaskStatus:', { taskId, status, url: `/developer/tasks/${taskId}/status` });
      const response = await ApiService.patch(`/developer/tasks/${taskId}/status`, { status });
      console.log('updateTaskStatus response:', response);
      return response;
    } catch (error: any) {
      console.error('Error updating task status:', {
        error,
        response: error.response?.data,
        status: error.response?.status,
        taskId,
        requestedStatus: status,
      });
      throw error;
    }
  }

  /**
   * Get task details
   */
  static async getTask(taskId: number): Promise<DeveloperTask> {
    try {
      const response = await ApiService.get<{
        success: boolean;
        data: DeveloperTask;
      }>(`/developer/tasks/${taskId}`);
      
      return response.data;
    } catch (error) {
      console.error('Error fetching task details:', error);
      throw new Error('Failed to load task details');
    }
  }

  /**
   * Get project details
   */
  static async getProject(projectId: number): Promise<DeveloperProject> {
    try {
      const response = await ApiService.get<{
        success: boolean;
        data: DeveloperProject;
      }>(`/developer/projects/${projectId}`);
      
      return response.data;
    } catch (error) {
      console.error('Error fetching project details:', error);
      throw new Error('Failed to load project details');
    }
  }

  /**
   * Get project specifications
   */
  static async getProjectSpecifications(projectId: number): Promise<{
    constitution?: string;
    specification?: string;
    technicalPlan?: string;
    tasks?: string;
    createdAt?: string;
    updatedAt?: string;
    approvedBy?: string;
  }> {
    try {
      const response = await ApiService.get<{
        success: boolean;
        data: {
          constitution?: string;
          specification?: string;
          technicalPlan?: string;
          tasks?: string;
          createdAt?: string;
          updatedAt?: string;
          approvedBy?: string;
        };
      }>(`/projects/${projectId}/specifications`);
      
      return response.data;
    } catch (error) {
      console.error('Error fetching project specifications:', error);
      throw new Error('Failed to load project specifications');
    }
  }

  /**
   * Log time for a task
   */
  static async logTime(taskId: number, data: {
    hours: number;
    date: string;
    description?: string;
  }): Promise<void> {
    try {
      await ApiService.post(`/developer/tasks/${taskId}/time`, data);
    } catch (error) {
      console.error('Error logging time:', error);
      throw new Error('Failed to log time');
    }
  }

  /**
   * Get time logs for a task
   */
  static async getTimeLogs(taskId: number): Promise<Array<{
    id: number;
    taskId: number;
    userId: number;
    userName: string;
    hours: number;
    date: string;
    description?: string;
    createdAt: string;
  }>> {
    try {
      const response = await ApiService.get<{
        success: boolean;
        data: Array<{
          id: number;
          taskId: number;
          userId: number;
          userName: string;
          hours: number;
          date: string;
          description?: string;
          createdAt: string;
        }>;
      }>(`/developer/tasks/${taskId}/time`);
      
      return response.data;
    } catch (error) {
      console.error('Error fetching time logs:', error);
      throw new Error('Failed to load time logs');
    }
  }

  /**
   * Get task comments
   */
  static async getTaskComments(taskId: number): Promise<Array<{
    id: number;
    taskId: number;
    userId: number;
    userName: string;
    userAvatar?: string;
    content: string;
    createdAt: string;
    updatedAt?: string;
  }>> {
    try {
      const response = await ApiService.get<{
        success: boolean;
        data: Array<{
          id: number;
          taskId: number;
          userId: number;
          userName: string;
          userAvatar?: string;
          content: string;
          createdAt: string;
          updatedAt?: string;
        }>;
      }>(`/developer/tasks/${taskId}/comments`);
      
      return response.data;
    } catch (error) {
      console.error('Error fetching task comments:', error);
      throw new Error('Failed to load task comments');
    }
  }

  /**
   * Add comment to task
   */
  static async addTaskComment(taskId: number, content: string): Promise<void> {
    try {
      await ApiService.post(`/developer/tasks/${taskId}/comments`, { content });
    } catch (error) {
      console.error('Error adding task comment:', error);
      throw new Error('Failed to add comment');
    }
  }

  /**
   * Update task details
   */
  static async updateTask(taskId: number, data: Partial<{
    title: string;
    description: string;
    priority: DeveloperTaskPriority;
    dueDate: string;
    estimatedHours: number;
  }>): Promise<DeveloperTask> {
    try {
      const response = await ApiService.patch<{
        success: boolean;
        data: DeveloperTask;
      }>(`/developer/tasks/${taskId}`, data);
      
      return response.data;
    } catch (error) {
      console.error('Error updating task:', error);
      throw new Error('Failed to update task');
    }
  }
}

export default DeveloperDashboardService;
